import Tutorial from "../UI/Tutorial";
import "./index.scss";
import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { selectStats, selectCurrentLevel } from "../../Store/store";
import { goBack, fetchImage, getSearchParams, shuffle } from "../nonUIFuncs";
import { useLocation, Link } from "react-router-dom";
import LazyFallback from "../UI/LazyFallback";
import PropTypes from "prop-types";

// Each game's code is its own download, kept out of the first screen's. The
// app fetches them all in the background once it has started (preloadGames,
// called from App), so a game is usually cached by the time a child opens it:
// waiting until a difficulty was pressed made kids sit on "Loading..." for
// 10-17 seconds on slower connections, queued behind the menus' images.
const gameLoaders = {
  WordToImage: () => import("../WordToImage"),
  Stories: () => import("../Stories"),
  WhackAMole: () => import("../WhackAMole"),
  Matching: () => import("../Matching"),
  GoodGuess: () => import("../GoodGuess"),
  Wordless: () => import("../Wordless"),
  BubblePop: () => import("../BubblePop"),
  Counting: () => import("../Counting"),
  VerbActivity: () => import("../VerbActivity"),
  FillInTheBlank: () => import("../FillInTheBlank"),
  SolveIt: () => import("../SolveIt"),
  Crocodile: () => import("../Crocodile"),
  ItemFall: () => import("../ItemFall"),
  FromMeToYou: () => import("../FromMeToYou"),
};
const WordToImage = lazy(gameLoaders.WordToImage);
const Stories = lazy(gameLoaders.Stories);
const WhackAMole = lazy(gameLoaders.WhackAMole);
const Matching = lazy(gameLoaders.Matching);
const GoodGuess = lazy(gameLoaders.GoodGuess);
const Wordless = lazy(gameLoaders.Wordless);
const BubblePop = lazy(gameLoaders.BubblePop);
const Counting = lazy(gameLoaders.Counting);
const VerbActivity = lazy(gameLoaders.VerbActivity);
const FillInTheBlank = lazy(gameLoaders.FillInTheBlank);
const SolveIt = lazy(gameLoaders.SolveIt);
const Crocodile = lazy(gameLoaders.Crocodile);
const ItemFall = lazy(gameLoaders.ItemFall);
const FromMeToYou = lazy(gameLoaders.FromMeToYou);

/** Fetch every game's code in the background (errors are ignored: a game that
 * fails here just loads when opened, as before). */
export const preloadGames = () =>
  Object.values(gameLoaders).forEach((load) => load().catch(() => {}));

/**
 * Game router component that renders the appropriate game component based on gameType.
 * Acts as a central switch to route to different mini-games within the application.
 *
 * @param {Object} props - Component props
 * @param {string} props.gameType - The type of game to render
 * @param {Function} props.checkWorld - Function to check game world state
 * @param {Function} props.updateLevelData - Function to update level progression
 * @param {React.ReactNode} props.children - Child components to pass to games
 * @param {Function} props.setProgress - Function to update game progress
 * @param {boolean} props.screenCover - Screen cover visibility state
 * @param {Function} props.setScreenCover - Function to control screen cover
 * @param {Function} props.findStars - Function to calculate star ratings
 * @param {Object} props.starsObj - Star calculation parameters
 * @param {Function} props.backToStart - Function to return to start screen
 * @param {number} props.currentLevel - Current game level
 * @param {number} props.currentLevelProgress - Current level progress
 * @param {Function} props.setCurrentLevel - Function to set current level
 * @param {Function} props.setCurrentLevelProgress - Function to set level progress
 * @param {Function} props.getWeightedData - Function to get weighted game data
 * @param {boolean} props.splitCat - Whether categories are split
 * @param {number} props.gameTime - Game time limit or duration
 * @returns {JSX.Element} The appropriate game component
 */
const GameSwitch = ({
  gameType,
  checkWorld,
  updateLevelData,
  children,
  setProgress,
  screenCover,
  setScreenCover,
  findStars,
  starsObj,
  backToStart,
  currentLevel,
  currentLevelProgress,
  setCurrentLevel,
  setCurrentLevelProgress,
  getWeightedData,
  splitCat,
  gameTime,
  dataset,
}) => {
  switch (gameType) {
    case "From Me To You":
      return (
        <FromMeToYou
          checkWorld={checkWorld}
          setProgress={setProgress}
          updateLevelData={updateLevelData}
          children={children}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          splitCat={splitCat}
          gameTime={gameTime}
          dataset={dataset}
        />
      );
    case "Item Fall":
      return (
        <ItemFall
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Crocodile":
      return (
        <Crocodile
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Solve It":
      return (
        <SolveIt
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Fill In The Blank":
      return (
        <FillInTheBlank
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Story Book":
      return (
        <Stories
          checkWorld={checkWorld}
          children={children}
          setProgress={setProgress}
          findStars={findStars}
          starsObj={starsObj}
          backToStart={backToStart}
          getWeightedData={getWeightedData}
        />
      );
    case "Word To Image":
      return (
        <WordToImage
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Whack-A-Mole":
      return (
        <WhackAMole
          children={children}
          setProgress={setProgress}
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Matching":
      return (
        <Matching
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          setProgress={setProgress}
          children={children}
          findStars={findStars}
          starsObj={starsObj}
          setScreenCover={setScreenCover}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Good Guess":
      return (
        <GoodGuess
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Wordless":
      return (
        <Wordless
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Bubble Pop":
      return (
        <BubblePop
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Counting":
      return (
        <Counting
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          findStars={findStars}
          starsObj={starsObj}
          currentLevel={currentLevel}
          currentLevelProgress={currentLevelProgress}
          setCurrentLevel={setCurrentLevel}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    case "Verb Activity":
      return (
        <VerbActivity
          checkWorld={checkWorld}
          updateLevelData={updateLevelData}
          children={children}
          setProgress={setProgress}
          setScreenCover={setScreenCover}
          findStars={findStars}
          starsObj={starsObj}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
      );
    default:
      return <div>No Game Found</div>;
  }
};

/** Shows LazyFallback while the chosen game's code downloads. */
const AllGameSwitcher = (props) => (
  <Suspense fallback={<LazyFallback />}>
    <GameSwitch {...props} />
    {/* first-play tutorial: once per device, "How to play" shows it again */}
    <Tutorial gameType={props.gameType} />
  </Suspense>
);
const GameContainer = ({
  setScreen,
  setGameTime,
  updateLevelData,
  setProgress,
  scoreType,
  gameTypeImage,
  findStars,
  starsObj,
  gameStyle,
  splitCat,
  gameTime,
  dataset,
}) => {
  const displayTimeRef = useRef(0);
  const currentLevelRef = useRef(0);
  const currentLevelProgressRef = useRef(0);
  const [displayTime, setDisplayTime] = useState(0);
  const [screenCover, setScreenCover] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentLevelProgress, setCurrentLevelProgress] = useState(0);
  const stats = useSelector(selectStats);
  const reduxCurrentLevel = useSelector(selectCurrentLevel);
  const location = useLocation();
  const params = getSearchParams(location);
  const difficultyParam = params.find((d) => d.id === "difficulty");

  displayTimeRef.current = displayTime;
  currentLevelProgressRef.current = currentLevelProgress;
  currentLevelRef.current = currentLevel;

  let gameType = location.search
    .replace("?", "")
    .split("&")
    .find((str) => str.includes("game-type="))
    ?.split("=")[1]
    .replaceAll("%20", " ")
    .replaceAll("%27", "'")
    .replaceAll("%E2%80%99", "’");
  useEffect(() => {
    let timer = setInterval(
      () => setDisplayTime(displayTimeRef.current + 1),
      1000,
    );
    if (gameType === "From Me To You") {
      clearInterval(timer);
    }
    return () => {
      clearInterval(timer);
    };
  }, []);
  let unitName = "";
  let Key = 1;
  let searchArr = location.search
    .replace("?", "")
    .split("&")
    .find((str) => str.includes("unit="));
  if (searchArr !== undefined) {
    unitName = searchArr
      .split("=")[1]
      .replaceAll("%20", " ")
      .replaceAll("%27", "'")
      .replaceAll("%E2%80%99", "’");
    if (isNaN(Number(unitName.slice(0, 2)))) {
      Key = Number(unitName.slice(0, 1));
      unitName = unitName.slice(1);
    } else {
      Key = Number(unitName.slice(0, 2));
      unitName = unitName.slice(2);
    }
  }
  const checkWorld = () => {
    setGameTime(displayTimeRef.current);
    setScreen("playAgainScreen");
  };
  const findStarsUpdated = (isStories) => {
    setGameTime(displayTimeRef.current);
    findStars(displayTimeRef.current, isStories);
  };

  const getWeightedData = ({ data, sliceNumber, history, getTarget }) => {
    let dif = 5;
    if (typeof Number(difficultyParam.value) === "number") {
      Number(difficultyParam.value);
    }
    // 1. The difficulty you choose at the beginning can help determine the likelyhood of words you see in the games.
    // The higher the word is ranked, the greater chance you have to see it in the game.
    let difData = [];
    for (let i = 1; i <= dif; i++) {
      let dataToPush = data.filter(
        (d) => Number(d.rank) === i || d.rank === undefined,
      );
      if (dataToPush.length > 0) {
        difData.push({ dif: i, data: dataToPush });
      }
    }
    for (let i = 0; i < difData.length; i++) {
      let dData = difData[i].data;
      // 1.5 using 'i' as the 'difficulty', find the rough number of chances you have to see this rank of data. (either 8, 16, 32, 64, or 128 based on i)
      let totalChances = 2 ** (i + 3);
      difData[i].data = dData.map((d) => {
        // 2. The more you get correct the lower the 'correctness' value is and the less likely you are to see it, the more you get it wrong, the more likely you are to see it.
        let correctnessDif =
          d.correctness > 0 ? "*" : d.correctness < 0 ? "/" : null;
        let correctnessVal = Math.abs(d.correctness);
        // between 'totalChances' and 'dData.length' we can scale the likelyhood of seeing the data at the higher ranks
        let weight = Math.ceil(totalChances / dData.length);
        if (correctnessDif !== null) {
          weight =
            correctnessDif === "*"
              ? Math.ceil(weight * (correctnessVal + 1))
              : Math.ceil(weight / (correctnessVal + 1));
        }
        return { ...d, weight: weight };
      });
    }
    // Re-flatten the data.
    let weightedData = [];
    difData.forEach((d) => {
      weightedData.push(...d.data);
    });
    // Use the 'weight' value to determine how many chances that vocab has to be seen in the data.
    let expandedData = [];
    weightedData.forEach((d) => {
      for (let i = 0; i < d.weight; i++) {
        expandedData.push(d);
      }
    });
    // 3. Impliment a History and don't repeat a word that exists in it.
    let targetData = [...expandedData];
    if (history !== undefined && data.length - history.length >= sliceNumber) {
      history.forEach((d) => {
        targetData = targetData.filter((datum) => d.English !== datum.English);
      });
    }
    let target = undefined;
    let slice = sliceNumber;
    if (getTarget) {
      slice = slice - 1;
      target = targetData[Math.floor(Math.random() * targetData.length)];
      expandedData = expandedData.filter((d) => d.English !== target.English);
    }
    let slicedData = [];
    for (let i = 0; i < slice; i++) {
      let randomNumber = Math.floor(Math.random() * expandedData.length);
      let dataPoint = expandedData[randomNumber];
      slicedData.push(dataPoint);
      expandedData = expandedData.filter(
        (d) => d.English !== dataPoint.English,
      );
    }
    let finalData = shuffle(slicedData);
    //TODO: Choose a target to pass back
    if (location.pathname.includes("endless-mode")) {
      finalData = shuffle(data).slice(
        0,
        getTarget ? sliceNumber - 1 : sliceNumber,
      );
    }
    return { data: finalData, target: target };
  };

  if (gameStyle === "blank") {
    return (
      <div>
        <Link
          to={{
            pathname: goBack(location).pathname,
            search: goBack(location).search,
          }}
          className="gamecontainer_backbutton"
        />
        <AllGameSwitcher
          setProgress={setProgress}
          checkWorld={checkWorld}
          gameType={typeof gameType === "string" ? gameType : ""}
          updateLevelData={updateLevelData}
          screenCover={screenCover}
          setScreenCover={setScreenCover}
          findStars={findStarsUpdated}
          starsObj={starsObj}
          backToStart={() => setScreen("playScreen")}
          currentLevel={currentLevelRef.current}
          setCurrentLevel={setCurrentLevel}
          currentLevelProgress={currentLevelProgressRef.current}
          setCurrentLevelProgress={setCurrentLevelProgress}
          getWeightedData={getWeightedData}
          dataset={dataset}
        />
        <div
          className={`gamecontainer_gametypebadgecontainer${
            scoreType === "time" &&
            !location.pathname.includes("/endless-mode/")
              ? "_alt"
              : ""
          }`}
        >
          <div
            className="gamecontainer_gametypebadge"
            style={{ backgroundImage: `url(${fetchImage(gameTypeImage)})` }}
          />
        </div>
        <div className="gamecontainer_statcontainer">
          {location.pathname.includes("/endless-mode/") ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">
                {reduxCurrentLevel}
              </h2>
              <div className="gamecontainer_statcontents_banner">
                <p>Level</p>
              </div>
            </div>
          ) : scoreType !== "time" ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">{stats.score}</h2>
              <div className="gamecontainer_statcontents_banner">
                <p id="levelNumber">Score</p>
              </div>
            </div>
          ) : (
            <div
              className="gamecontainer_statcontents"
              style={{ opacity: 0 }}
            />
          )}
          {scoreType !== "normal" ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">
                {displayTimeRef.current}
              </h2>
              <div className="gamecontainer_statcontents_banner">
                <p>Time</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className={`gamecontainer_container${gameStyle === "scene" ? " gamecontainer_container--scene" : ""}`}>
        <div className="gamecontainer_container_background" />
        {/* UI Start */}
        {/* Top Banner */}
        {!location.pathname.includes("/endless-mode/") ? (
          <div className="gamecontainer_topbanner">
            <div className="gamecontainer_topbanner_contents">
              <div
                className="gamecontainer_topbanner_badge"
                style={{
                  backgroundImage: `url(${fetchImage(
                    `Common_Category_Badge_${Key}1.png`,
                  )})`,
                }}
              />
              <h1 className="gamecontainer_topbanner_headertext">{unitName}</h1>
              <div
                className="gamecontainer_topbanner_badge"
                style={{
                  backgroundImage: `url(${fetchImage(
                    `Common_Category_Badge_${Key}1.png`,
                  )})`,
                }}
              />
            </div>
          </div>
        ) : null}
        {/* Back Button */}
        <Link
          to={{
            pathname: goBack(location).pathname,
            search: goBack(location).search,
          }}
          className="gamecontainer_backbutton"
        />
        {/* UI End */}
        <div className="gamecontainer_contents">
          <AllGameSwitcher
            setProgress={setProgress}
            checkWorld={checkWorld}
            gameType={typeof gameType === "string" ? gameType : ""}
            updateLevelData={updateLevelData}
            screenCover={screenCover}
            setScreenCover={setScreenCover}
            findStars={findStarsUpdated}
            starsObj={starsObj}
            currentLevel={currentLevelRef.current}
            setCurrentLevel={setCurrentLevel}
            currentLevelProgress={currentLevelProgressRef.current}
            setCurrentLevelProgress={setCurrentLevelProgress}
            getWeightedData={getWeightedData}
            gameTime={displayTimeRef.current}
            dataset={dataset}
            // children={

            // }
          />
        </div>
        <div
          className={`gamecontainer_gametypebadgecontainer${
            scoreType === "time" &&
            !location.pathname.includes("/endless-mode/")
              ? "_alt"
              : ""
          }`}
        >
          <div
            className="gamecontainer_gametypebadge"
            style={{ backgroundImage: `url(${fetchImage(gameTypeImage)})` }}
          />
        </div>
        <div className="gamecontainer_statcontainer">
          {location.pathname.includes("/endless-mode/") ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">
                {reduxCurrentLevel}
              </h2>
              <div className="gamecontainer_statcontents_banner">
                <p>Level</p>
              </div>
            </div>
          ) : scoreType !== "time" ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">{stats.score}</h2>
              <div className="gamecontainer_statcontents_banner">
                <p id="levelNumber">Score</p>
              </div>
            </div>
          ) : (
            <div
              className="gamecontainer_statcontents"
              style={{ opacity: 0 }}
            />
          )}
          {scoreType !== "normal" && gameType !== "From Me To You" ? (
            <div className="gamecontainer_statcontents">
              <h2 className="gamecontainer_statcontents_stat">
                {displayTimeRef.current}
              </h2>
              <div className="gamecontainer_statcontents_banner">
                <p>Time</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {screenCover &&
      (gameType === "Wordless" ||
        gameType === "Word To Image" ||
        gameType === "Matching") ? (
        <div className="timeoutdiv" onClick={() => setScreenCover(false)} />
      ) : null}
    </div>
  );
};

export default GameContainer;

GameContainer.propTypes = {
  setScreen: PropTypes.func,
  setGameTime: PropTypes.func,
  updateLevelData: PropTypes.func,
  setProgress: PropTypes.func,
  scoreType: PropTypes.string,
  gameTypeImage: PropTypes.string,
  findStars: PropTypes.func,
  starsObj: PropTypes.object,
  dataset: PropTypes.string,
};

AllGameSwitcher.propTypes = {
  gameType: PropTypes.string,
  checkWorld: PropTypes.func,
  updateLevelData: PropTypes.func,
  children: PropTypes.object,
  setProgress: PropTypes.func,
  screenCover: PropTypes.bool,
  setScreenCover: PropTypes.func,
  findStars: PropTypes.func,
  starsObj: PropTypes.object,
  dataset: PropTypes.string,
};
